import { createContext, useContext } from "react";
import type { ComponentType, ReactNode } from "react";

// Format: { THEME_NAME: CSS_SELECTOR }
export const CHART_THEMES = { light: "", dark: ".dark" } as const;

type ChartSeriesThemeColors = Record<keyof typeof CHART_THEMES, string>;

/** A series is colored either with one `color` for both themes, or a per-theme `theme` map. */
type ChartSeriesColor = { color?: string; theme?: never } | { color?: never; theme: ChartSeriesThemeColors };

interface ChartSeriesDisplay {
  label?: ReactNode;
  icon?: ComponentType;
}

/**
 * Per-series display config, keyed by series key (a dataKey or category name): an optional
 * label/icon plus either a single `color` or a per-theme `theme` map. Colors surface as
 * `--color-<key>` CSS variables inside the chart, usable as `var(--color-<key>)` fills.
 */
export type ChartConfig = {
  [key in string]: ChartSeriesDisplay & ChartSeriesColor;
};

interface ChartContextValue {
  config: ChartConfig;
}

export const ChartContext = createContext<ChartContextValue | null>(null);

export const useChartContext = (): ChartContextValue => {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error("useChartContext must be used within a <Chart />");
  }
  return context;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/** The config entry for a recharts tooltip/legend payload item: the item's own value under `key`
 *  names the series when present (categorical charts), else `key` itself is the series key. */
export const getPayloadConfigFromPayload = (
  config: ChartConfig,
  payload: unknown,
  key: string
): ChartConfig[string] | undefined => {
  if (!isRecord(payload)) return undefined;
  const inner = isRecord(payload.payload) ? payload.payload : undefined;
  const direct = payload[key];
  const nested = inner?.[key];
  const configLabelKey = typeof direct === "string" ? direct : typeof nested === "string" ? nested : key;
  return configLabelKey in config ? config[configLabelKey] : config[key];
};
